import type {
	SetupStep,
	SetupStepKey,
	SetupStepStatus,
} from "@/types/api/onboarding";

/**
 * The eight Tenant Setup steps (design §6 item 12) and how each one derives
 * its status chip from the nine `setup-status` gates (G1–G9). A step's chip
 * is the worst status among its gates (Red > Amber > Green; NotApplicable is
 * ignored); a step with no loaded gates shows no chip.
 *
 * `?step=` search-param values are the `SetupStepId`s, so a readiness gate
 * (or any external link) can deep-link straight into a step.
 */
export const SETUP_STEP_IDS = [
	"company",
	"locations",
	"checklists",
	"assets",
	"users",
	"inspection-configurations",
	"notifications",
	"go-live",
] as const;

export type SetupStepId = (typeof SETUP_STEP_IDS)[number];

export const DEFAULT_SETUP_STEP: SetupStepId = "company";

export interface SetupStepDefinition {
	id: SetupStepId;
	/** 1-based position shown in the rail. */
	number: number;
	title: string;
	shortTitle: string;
	description: string;
	/** Readiness gate keys whose worst status becomes the step chip. */
	gates: readonly SetupStepKey[];
}

export const SETUP_STEPS: readonly SetupStepDefinition[] = [
	{
		id: "company",
		number: 1,
		title: "Company & authentication",
		shortTitle: "Company",
		description:
			"The tenant record, its owner, industry, association and sign-in mode as created on the Create Client page.",
		gates: ["owner-admin"],
	},
	{
		id: "locations",
		number: 2,
		title: "Locations",
		shortTitle: "Locations",
		description:
			"One row per yard or site. Assets, location-scoped notification recipients and QR scans all resolve to a location.",
		gates: ["locations"],
	},
	{
		id: "checklists",
		number: 3,
		title: "Checklists & asset groups",
		shortTitle: "Checklists",
		description:
			"Bring in the checklists (starter pack, library, JSON). Wiring a checklist creates its asset group, inspection configuration and inspector group — the asset import in the next step needs the groups.",
		gates: ["layout-modes"],
	},
	{
		id: "assets",
		number: 4,
		title: "Assets",
		shortTitle: "Assets",
		description:
			"Every asset joins an asset group from step 3 — add them one at a time or import a spreadsheet. The group's configuration is what a QR scan resolves to.",
		gates: ["asset-group-configurations", "asset-status"],
	},
	{
		id: "users",
		number: 5,
		title: "Users & groups",
		shortTitle: "Users",
		description:
			"Users are created held (no e-mail). Operators must be members of a group that is an inspector group of a configuration, or they will see no inspections.",
		gates: ["inspector-groups"],
	},
	{
		id: "inspection-configurations",
		number: 6,
		title: "Inspection configurations",
		shortTitle: "Configurations",
		description:
			"Binds a checklist to asset groups and inspector groups. Read-only here — edit in the client app.",
		gates: ["asset-group-configurations", "inspector-groups"],
	},
	{
		id: "notifications",
		number: 7,
		title: "Notification recipients",
		shortTitle: "Recipients",
		description:
			"Who gets e-mailed when an inspection raises an issue: one assignment per issue role (Owner company-wide; General Manager per yard).",
		gates: ["issue-routing"],
	},
	{
		id: "go-live",
		number: 8,
		title: "Go-live",
		shortTitle: "Go-live",
		description:
			"All nine gates, the held-user list and the invitation batch — the only step that e-mails the customer.",
		gates: [
			"owner-admin",
			"locations",
			"asset-group-configurations",
			"inspector-groups",
			"issue-routing",
			"layout-modes",
			"asset-status",
			"held-users",
			"notification-config",
		],
	},
];

export const SETUP_STEP_BY_ID: Record<SetupStepId, SetupStepDefinition> =
	Object.fromEntries(SETUP_STEPS.map((s) => [s.id, s])) as Record<
		SetupStepId,
		SetupStepDefinition
	>;

export function isSetupStepId(value: unknown): value is SetupStepId {
	return (
		typeof value === "string" &&
		(SETUP_STEP_IDS as readonly string[]).includes(value)
	);
}

const STATUS_RANK: Record<SetupStepStatus, number> = {
	Green: 0,
	NotApplicable: 0,
	Amber: 1,
	Red: 2,
};

/** Worst status among `keys` (undefined when none of them is loaded). */
export function worstStatus(
	steps: readonly SetupStep[] | undefined,
	keys: readonly string[],
): SetupStepStatus | undefined {
	if (!steps) return undefined;
	let worst: SetupStepStatus | undefined;
	for (const step of steps) {
		if (!keys.includes(step.key)) continue;
		const status = step.status as SetupStepStatus;
		if (!(status in STATUS_RANK)) continue;
		if (worst === undefined || STATUS_RANK[status] > STATUS_RANK[worst]) {
			worst = status;
		}
	}
	return worst;
}

/**
 * Where `/client/onboarding` lands when no `?step=` is given: the first step
 * (rail order) with a Red gate, else the first with an Amber gate, else
 * Company. Go-live owns every gate, so it is only chosen when the sole
 * problem is one of the gates no earlier step owns (held users,
 * notification config).
 */
export function landingStep(
	steps: readonly SetupStep[] | undefined,
): SetupStepId {
	if (!steps || steps.length === 0) return DEFAULT_SETUP_STEP;
	for (const wanted of ["Red", "Amber"] as const) {
		for (const step of SETUP_STEPS) {
			if (worstStatus(steps, step.gates) === wanted) return step.id;
		}
	}
	return DEFAULT_SETUP_STEP;
}

/** The gates that feed one step, in the order the server returned them. */
export function gatesForStep(
	steps: readonly SetupStep[] | undefined,
	stepId: SetupStepId,
): SetupStep[] {
	const keys = SETUP_STEP_BY_ID[stepId].gates;
	return (steps ?? []).filter((s) => keys.includes(s.key as SetupStepKey));
}

/**
 * Where a readiness gate should send the admin now that the console is a
 * stepper: first by gate key (the step that owns the fix), then by the admin
 * route the server still emits. Returns null for gates whose admin route has
 * no step (e.g. the layout-mode grants grid) and for client-app links.
 */
const GATE_TO_STEP: Partial<Record<string, SetupStepId>> = {
	"owner-admin": "users",
	locations: "locations",
	"asset-group-configurations": "assets",
	"inspector-groups": "users",
	"issue-routing": "notifications",
	"asset-status": "assets",
	"held-users": "go-live",
};

const ADMIN_ROUTE_TO_STEP: Partial<Record<string, SetupStepId>> = {
	"/client/users": "users",
	"/client/locations": "locations",
	"/client/assets": "assets",
	"/client/asset-groups": "checklists",
	"/client/groups": "users",
};

export function stepForGate(step: SetupStep): SetupStepId | null {
	if (step.deepLinkApp === "client") return null;
	const byKey = GATE_TO_STEP[step.key];
	if (byKey) return byKey;
	const link = step.deepLink?.trim();
	if (!link) return null;
	const path = link.split("?")[0].replace(/\/+$/, "");
	return ADMIN_ROUTE_TO_STEP[path] ?? null;
}
